import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import axios from "axios";
import vuetify from "./plugins/vuetify";
import constants from "./plugins/Constants";
import moment from "moment";
import rateLimitService from "./services/rateLimitService";
import { normalizeEndpoint, shouldTrackEndpoint } from "./utils/rateLimitUtils";

Vue.config.productionTip = false;
Vue.prototype.$http = axios;
Vue.prototype.$constants = constants;
Vue.prototype.moment = moment;
Vue.prototype.$rateLimitService = rateLimitService;

// Dynamically set the X-Parent-Origin header for all requests
axios.interceptors.request.use(
	(config) => {
		// Determine the parent origin
		const parentOrigin =
			window.location.ancestorOrigins?.[0] || (document.referrer ? new URL(document.referrer).origin : null);

		if (parentOrigin) {
			// Attach the X-Parent-Origin header
			config.headers["X-Parent-Origin"] = parentOrigin;
		}

		return config;
	},
	(error) => {
		// Handle request errors
		return Promise.reject(error);
	}
);

// Create an event bus for rate limit notifications
Vue.prototype.$rateLimitBus = new Vue();

// Add response interceptor for rate limiting
axios.interceptors.response.use(
	(response) => {
		// Extract endpoint for tracking
		const endpoint = normalizeEndpoint(response.config.method, response.config.url);

		// Check if we should track this endpoint
		if (shouldTrackEndpoint(endpoint)) {
			// Check for rate limit headers in successful responses
			const remaining =
				response.headers["x-ratelimit-remaining"] || response.headers["x-rate-limit-remaining"];
			const limit = response.headers["x-ratelimit-limit"] || response.headers["x-rate-limit-limit"];

			if (remaining !== undefined) {
				// Update endpoint-specific limits
				store.dispatch("rateLimiting/updateEndpointLimits", {
					endpoint,
					remaining: parseInt(remaining),
					limit: limit ? parseInt(limit) : null,
				});

				// Also update global limits for backward compatibility
				store.dispatch("rateLimiting/updateLimits", {
					remaining: parseInt(remaining),
					limit: limit ? parseInt(limit) : null,
				});
			}

			// Clear endpoint-specific rate limit if getting successful responses
			if (store.getters["rateLimiting/isEndpointRateLimited"](endpoint)) {
				store.dispatch("rateLimiting/clearEndpointRateLimit", endpoint);
			}

			// Also clear global rate limit for backward compatibility
			if (store.state.rateLimiting.isRateLimited) {
				store.dispatch("rateLimiting/clearRateLimit");
			}

			// Clear requests remaining warning if we're back to a healthy level
			if (remaining !== undefined && parseInt(remaining) > 5) {
				store.commit("rateLimiting/UPDATE_REQUEST_LIMITS", {
					remaining: null,
					limit: null,
				});
			}
		}

		return response;
	},
	(error) => {
		// Handle 429 rate limit errors
		if (error.response?.status === 429) {
			const endpoint = normalizeEndpoint(error.config.method, error.config.url);
			const retryAfter =
				error.response.headers["retry-after"] || error.response.headers["x-ratelimit-retry-after"] || 60;

			// Extract rate limit details from response
			const isLockedOut = error.response.data?.isLockedOut || false;
			const lockoutDuration = error.response.data?.lockoutDuration || null;
			const violatedLimit = error.response.data?.violatedLimit || null;

			// Extract error message
			let message = "You have exceeded the maximum number of requests. Please wait before trying again.";
			if (error.response.data?.message) {
				message = error.response.data.message;
			} else if (error.response.data?.error) {
				message = error.response.data.error;
			}

			// Update endpoint-specific rate limit
			if (shouldTrackEndpoint(endpoint)) {
				store.dispatch("rateLimiting/handleEndpointRateLimited", {
					endpoint,
					retryAfter: parseInt(retryAfter),
					isLockedOut,
					lockoutDuration,
					violatedLimit,
					message,
					request: error.config,
				});

				// Emit endpoint-specific event
				Vue.prototype.$rateLimitBus.$emit("endpoint-rate-limited", {
					endpoint,
					timestamp: Date.now(),
					retryAfter: parseInt(retryAfter),
					isLockedOut,
					lockoutDuration,
				});
			}

			// Also update global rate limit for backward compatibility
			store.dispatch("rateLimiting/handleRateLimited", {
				retryAfter: parseInt(retryAfter),
				isLockedOut,
				lockoutDuration,
				violatedLimit,
				message,
				request: error.config,
			});

			// Emit global event for backward compatibility
			Vue.prototype.$rateLimitBus.$emit("rate-limit-triggered", {
				timestamp: Date.now(),
				retryAfter: parseInt(retryAfter),
			});

			// Optional: Return a custom error or queue for retry
			// For now, we'll let the calling code handle it
		}

		return Promise.reject(error);
	}
);

new Vue({
	router,
	store,
	vuetify,
	render: (h) => h(App),
}).$mount("#app");
